<template>
  <section class="page-section">
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input v-model="filters.search" placeholder="按用品名称搜索" clearable class="toolbar-input" />
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
    </div>

    <el-table :data="supplies.content" class="business-table">
      <el-table-column prop="name" label="用品名称" min-width="150" />
      <el-table-column prop="spec" label="规格" min-width="140" />
      <el-table-column prop="stock" label="剩余库存" width="100" />
      <el-table-column prop="price" label="单价" width="100">
        <template #default="{ row }">¥ {{ row.price }}</template>
      </el-table-column>
      <el-table-column prop="location" label="存放位置" min-width="140" />
      <el-table-column label="预警" width="120">
        <template #default="{ row }">
          <el-tag :type="row.warning ? 'danger' : 'success'">
            {{ row.warning ? '库存紧张' : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="130">
        <template #default="{ row }">
          <el-button type="primary" link @click="openApplyDialog(row)">发起申领</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="supplies.totalElements > 0" class="pager">
      <span class="pager-text">第 {{ filters.page }} / {{ totalPages }} 页</span>
      <el-button :disabled="filters.page <= 1" @click="handlePrevPage">
        <el-icon><ArrowLeft /></el-icon>
        上一页
      </el-button>
      <el-button type="primary" :disabled="filters.page >= totalPages" @click="handleNextPage">
        下一页
        <el-icon><ArrowRight /></el-icon>
      </el-button>
    </div>

    <el-dialog v-model="dialogVisible" title="发起用品申领" width="460px">
      <el-form ref="applyFormRef" :model="applyForm" :rules="applyRules" label-position="top">
        <el-form-item label="用品名称">
          <el-input :model-value="selectedSupply.name" disabled />
        </el-form-item>
        <el-form-item label="当前库存">
          <el-input :model-value="String(selectedSupply.stock || 0)" disabled />
        </el-form-item>
        <el-form-item label="申领数量" prop="quantity">
          <el-input-number v-model="applyForm.quantity" :max="selectedSupply.stock || 1" class="full-width" />
        </el-form-item>
        <el-form-item label="用途说明" prop="reason">
          <el-input v-model="applyForm.reason" type="textarea" :rows="4" maxlength="120" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitApply">提交申领</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import {
  createEmployeeRequest,
  fetchEmployeeSupplies
} from '../../services/employeeService'

export default {
  name: 'EmployeeSuppliesPanel',
  components: {
    ArrowLeft,
    ArrowRight
  },
  data() {
    return {
      supplies: {
        content: [],
        totalElements: 0
      },
      filters: {
        search: '',
        page: 1,
        pageSize: 10
      },
      dialogVisible: false,
      selectedSupply: {},
      applyForm: {
        quantity: 1,
        reason: ''
      },
      applyRules: {
        quantity: [
          { required: true, message: '请输入申领数量', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              const quantity = Number(value)

              if (!Number.isFinite(quantity) || quantity === 0) {
                callback(new Error('申领数量不能为 0'))
                return
              }

              callback()
            },
            trigger: 'change'
          }
        ],
        reason: [{ required: true, message: '请输入用途说明', trigger: 'blur' }]
      }
    }
  },
  computed: {
    totalPages() {
      const total = Number(this.supplies.totalElements) || 0
      const pageSize = Number(this.filters.pageSize) || 10

      return Math.max(1, Math.ceil(total / pageSize))
    }
  },
  mounted() {
    this.loadSupplies()
  },
  methods: {
    async loadSupplies() {
      const response = await fetchEmployeeSupplies(this.filters)
      this.supplies = response.data
    },
    handlePageChange(page) {
      this.filters.page = page
      this.loadSupplies()
    },
    handlePrevPage() {
      if (this.filters.page <= 1) {
        return
      }

      this.handlePageChange(this.filters.page - 1)
    },
    handleNextPage() {
      if (this.filters.page >= this.totalPages) {
        return
      }

      this.handlePageChange(this.filters.page + 1)
    },
    handleSearch() {
      this.filters.page = 1
      this.loadSupplies()
    },
    handleReset() {
      this.filters.search = ''
      this.filters.page = 1
      this.loadSupplies()
    },
    openApplyDialog(row) {
      this.selectedSupply = row
      this.applyForm = {
        quantity: 1,
        reason: ''
      }
      this.dialogVisible = true
    },
    async submitApply() {
      const valid = await this.$refs.applyFormRef.validate().catch(() => false)

      if (!valid) {
        return
      }

      try {
        const response = await createEmployeeRequest(this.selectedSupply.id, this.applyForm.quantity, this.applyForm.reason)
        ElMessage.success(response.message || response.msg || '申领提交成功')
        this.dialogVisible = false
        this.loadSupplies()
      } catch (error) {
        ElMessage.error(error.message)
      }
    }
  }
}
</script>

<style scoped>
.page-section {
  display: grid;
  gap: 18px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-input {
  width: 260px;
}

.business-table {
  border-radius: 20px;
  overflow: hidden;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pager-text {
  color: #475569;
  font-size: 14px;
  font-weight: 600;
}

.full-width {
  width: 100%;
}

@media (max-width: 768px) {
  .toolbar,
  .toolbar-left,
  .toolbar-input {
    width: 100%;
  }
}
</style>
